import { useState } from 'react'
import type { ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { BookOpen, Database, FileQuestion, Search, SearchX } from 'lucide-react'

import { conceptsQueryOptions } from '#/api/concepts'
import { corpusItemsQueryOptions } from '#/api/corpus'
import type { CorpusItem } from '#/api/corpus'
import { platformQuestionnairesQueryOptions } from '#/api/questionnaire-guidance'
import { PageHeader, SectionLabel } from '#/components/page-header'
import { RouteError } from '#/components/route-error'
import { RoutePending } from '#/components/route-pending'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '#/components/ui/input-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '#/components/ui/select'
import {
  CORPUS_TABS,
  ConceptRow,
  CorpusList,
  GuidanceRow,
  HoldingRow,
} from '#/components/corpus/corpus-list'
import type { CorpusTab } from '#/components/corpus/corpus-list'
import { CorpusItemDialog } from '#/components/corpus/corpus-item-dialog'
import { useCorpusLabels } from '#/components/corpus/labels'
import { EmptyState } from '#/components/empty-state'
import { PillTabs } from '#/components/pill-tabs'
import { PageContainer } from '#/components/page-container'
import { Skeleton } from '#/components/ui/skeleton'

type CorpusSearch = { tab?: CorpusTab; theme?: string }

export const Route = createFileRoute('/_authed/accounts/$accountId/corpus/')({
  validateSearch: (search: Record<string, unknown>): CorpusSearch => ({
    tab: CORPUS_TABS.find((tab) => tab === search.tab),
    theme: typeof search.theme === 'string' ? search.theme : undefined,
  }),
  // The questionnaire list is a graph read the other two lists don't need, so
  // it starts here without holding the page: only the Guidance tab waits on it.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(platformQuestionnairesQueryOptions)
    return Promise.all([
      context.queryClient.ensureQueryData(conceptsQueryOptions),
      context.queryClient.ensureQueryData(corpusItemsQueryOptions),
    ])
  },
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: CorpusPage,
})

// Radix Select takes no empty value, so "every theme" has a name of its own.
const ALL_THEMES = 'all'

// What Annwn knows: its concepts, the datasets it reads against and the
// questionnaire guidance it answers to.
function CorpusPage() {
  const { t } = useTranslation()
  const { accountId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const tab = search.tab ?? 'concepts'
  const [query, setQuery] = useState('')
  const [openItem, setOpenItem] = useState<CorpusItem | null>(null)
  const { codeName, conceptName } = useCorpusLabels()

  const { data: concepts } = useSuspenseQuery(conceptsQueryOptions)
  const { data: items } = useSuspenseQuery(corpusItemsQueryOptions)
  const questionnaires = useQuery({
    ...platformQuestionnairesQueryOptions,
    throwOnError: tab === 'guidance',
  })
  const datasets = items.filter((item) => item.kind === 'dataset')
  const counts: Record<CorpusTab, number | undefined> = {
    concepts: concepts.length,
    dataset: datasets.length,
    guidance: questionnaires.data?.length,
  }

  // The open tab's rows, as what the filters read.
  const rowsFor = (section: CorpusTab): ReadonlyArray<Row> => {
    if (section === 'concepts') {
      return concepts.map((concept) => ({
        theme: concept.meta.theme,
        sample: false,
        text: `${conceptName(concept.id)} ${concept.summary ?? ''}`.toLowerCase(),
        node: (
          <ConceptRow
            key={concept.id}
            accountId={accountId}
            concept={concept}
          />
        ),
      }))
    }
    if (section === 'guidance') {
      return (questionnaires.data ?? []).map((questionnaire) => ({
        theme: null,
        sample: false,
        text: `${questionnaire.name} ${questionnaire.code}`.toLowerCase(),
        node: (
          <GuidanceRow
            key={questionnaire.code}
            accountId={accountId}
            questionnaire={questionnaire}
          />
        ),
      }))
    }
    return datasets.map((item) => ({
      theme: item.theme,
      sample: item.sample,
      text: `${item.name} ${item.summary} ${item.publisher}`.toLowerCase(),
      node: (
        <HoldingRow
          key={item.id}
          item={item}
          onOpen={() => setOpenItem(item)}
        />
      ),
    }))
  }
  const rows = rowsFor(tab)

  // A theme filter is offered only where it narrows: the section's rows span
  // more than one theme. A theme in the URL this section doesn't hold is
  // ignored rather than leaving the list empty.
  const themes = [
    ...new Set(rows.flatMap((row) => (row.theme ? [row.theme] : []))),
  ]
    .map((id) => ({ id, name: codeName('themes', id) }))
    .sort((a, b) => a.name.localeCompare(b.name))
  const theme =
    themes.length > 1 && themes.some(({ id }) => id === search.theme)
      ? search.theme
      : undefined
  const needle = query.trim().toLowerCase()
  const matches = (row: Row) =>
    (!theme || row.theme === theme) && (!needle || row.text.includes(needle))

  const setSearch = (next: CorpusSearch) =>
    navigate({ search: next, replace: true })

  return (
    <PageContainer width="wide">
      <PageHeader title={t('corpus.title')} />

      <div className="flex flex-col gap-6">
        {/* From `lg`, where the tabs and the filters fit side by side, the tabs'
            hairline runs on under the filters, so the row reads as one bar, the
            filters standing clear of it in line with the tab labels. */}
        <div className="flex flex-col gap-x-6 gap-y-4 lg:flex-row lg:items-end lg:justify-between lg:shadow-[inset_0_-1px_0_var(--color-border)]">
          <PillTabs
            variant="underline"
            size="lg"
            tabs={CORPUS_TABS.map((id) => ({
              id,
              label: t(`corpus.tabs.${id}`),
              count: counts[id],
            }))}
            active={tab}
            onActiveChange={(next) => {
              setQuery('')
              setSearch({ tab: next === 'concepts' ? undefined : next })
            }}
            label={t('corpus.sectionsLabel')}
            idPrefix="corpus-tab"
          />
          <div className="flex gap-2 lg:mb-2.5">
            {themes.length > 1 ? (
              <ThemeSelect
                themes={themes}
                theme={theme}
                onSelect={(next) => setSearch({ tab: search.tab, theme: next })}
              />
            ) : null}
            <InputGroup className="min-w-0 flex-1 bg-card sm:w-64 sm:flex-none">
              <InputGroupAddon>
                <Search aria-hidden="true" />
              </InputGroupAddon>
              <InputGroupInput
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t(`corpus.search.${tab}`)}
                aria-label={t(`corpus.search.${tab}`)}
              />
            </InputGroup>
          </div>
        </div>

        <div
          role="tabpanel"
          aria-labelledby={`corpus-tab-${tab}`}
          className="flex flex-col gap-8"
        >
          {tab === 'guidance' && questionnaires.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Section
              tab={tab}
              rows={rows.filter(matches)}
              filtering={Boolean(theme || needle)}
            />
          )}
        </div>
      </div>

      <CorpusItemDialog
        accountId={accountId}
        item={openItem}
        onOpenChange={(open) => {
          if (!open) setOpenItem(null)
        }}
      />
    </PageContainer>
  )
}

// One thing a section lists: what the theme filter and search read (`text`
// lower-cased), whether it is a sample, and the row it renders as.
type Row = {
  theme: string | null
  sample: boolean
  text: string
  node: ReactNode
}

const EMPTY_ICON = {
  concepts: BookOpen,
  dataset: Database,
  guidance: FileQuestion,
} as const

// A section's list: what Annwn really holds, then the samples, set apart under
// their own label.
function Section({
  tab,
  rows,
  filtering,
}: {
  tab: CorpusTab
  rows: ReadonlyArray<Row>
  filtering: boolean
}) {
  const { t } = useTranslation()
  if (filtering && rows.length === 0) {
    return <EmptyState icon={SearchX} title={t('corpus.noMatches')} />
  }
  const real = rows.filter((row) => !row.sample)
  const samples = rows.filter((row) => row.sample)
  return (
    <>
      {real.length > 0 ? (
        <CorpusList label={t(`corpus.tabs.${tab}`)}>
          {real.map((row) => row.node)}
        </CorpusList>
      ) : filtering ? null : (
        <EmptyState icon={EMPTY_ICON[tab]} title={t(`corpus.empty.${tab}`)} />
      )}
      {samples.length > 0 ? (
        <div className="flex flex-col gap-3">
          <SectionLabel>{t('corpus.samples')}</SectionLabel>
          <CorpusList label={t('corpus.samples')}>
            {samples.map((row) => row.node)}
          </CorpusList>
        </div>
      ) : null}
    </>
  )
}

function ThemeSelect({
  themes,
  theme,
  onSelect,
}: {
  themes: ReadonlyArray<{ id: string; name: string }>
  theme: string | undefined
  onSelect: (theme: string | undefined) => void
}) {
  const { t } = useTranslation()
  return (
    <Select
      value={theme ?? ALL_THEMES}
      onValueChange={(value) =>
        onSelect(value === ALL_THEMES ? undefined : value)
      }
    >
      <SelectTrigger
        aria-label={t('corpus.themesLabel')}
        className="shrink-0 bg-card"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        <SelectItem value={ALL_THEMES}>{t('corpus.allThemes')}</SelectItem>
        {themes.map(({ id, name }) => (
          <SelectItem key={id} value={id}>
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
